import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { Division, DivisionId } from '@atlas/shared';
import { DIVISIONS, canReachDivision, getDivision, isDivisionId } from '@atlas/shared';
import { useAuth } from './AuthContext';
import { users as usersApi } from '../services/api';

interface DivisionContextValue {
  /** The user's active (chosen) division, or null when none is set yet. */
  division: DivisionId | null;
  /** The full division definition for `division`, or null. */
  currentDivision: Division | null;
  /** Persist a new division choice (optimistic; PUTs to the server). No-ops if unreachable. */
  setDivision: (id: DivisionId) => void;
  /**
   * Whether the server value has been resolved for the current user. The route
   * gate waits on this so it never redirects to the launch screen on a
   * transient null during the initial load.
   */
  isLoaded: boolean;
  /**
   * Divisions the current user may reach (their assigned set; all six for org
   * members). This is what the launch screen and switcher offer — a user must
   * not be able to select a division they aren't assigned to.
   */
  availableDivisions: readonly Division[];
  /** Full division catalog (display order), regardless of reach. */
  divisions: readonly Division[];
}

const DivisionContext = createContext<DivisionContextValue | undefined>(undefined);

/** Per-user localStorage cache key — lets us hydrate instantly and avoid a flash. */
function cacheKey(userId: string): string {
  return `atlas-division:${userId}`;
}

function readCached(userId: string): DivisionId | null {
  if (typeof window === 'undefined') return null;
  const stored = window.localStorage.getItem(cacheKey(userId));
  return isDivisionId(stored) ? stored : null;
}

export function DivisionProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [division, setDivisionState] = useState<DivisionId | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  // Tracks which user we've already reconciled with the server, so switching
  // users (logout/login) re-runs the load.
  const loadedUserRef = useRef<string | null>(null);

  // Reachable = the user's assigned set (org members reach all; an absent claim
  // resolves to org → all, so a pre-claim session isn't locked out).
  const availableDivisions = useMemo(
    () => DIVISIONS.filter((d) => canReachDivision(user?.divisions, d.id)),
    [user?.divisions],
  );

  useEffect(() => {
    const currentUser = user?.userId ?? null;

    if (!currentUser) {
      loadedUserRef.current = null;
      setDivisionState(null);
      setIsLoaded(false);
      return;
    }

    if (loadedUserRef.current === currentUser) return;
    loadedUserRef.current = currentUser;

    const reachableIds = DIVISIONS
      .filter((d) => canReachDivision(user?.divisions, d.id))
      .map((d) => d.id);

    // Optimistic hydrate from the per-user cache — but only if still reachable —
    // so the gate/switcher paint immediately for returning users.
    const cached = readCached(currentUser);
    const cachedReachable = cached && reachableIds.includes(cached) ? cached : null;
    setDivisionState(cachedReachable);
    setIsLoaded(cachedReachable !== null);

    let cancelled = false;
    (async () => {
      try {
        const { division: serverValue } = await usersApi.getDivision();
        if (cancelled) return;

        // Prefer the server value, else the local cache; keep only if reachable.
        const candidate =
          (serverValue && isDivisionId(serverValue) && serverValue) || cachedReachable || null;
        let next: DivisionId | null =
          candidate && reachableIds.includes(candidate) ? candidate : null;

        // Auto-pick when the user can reach exactly one division — no point
        // showing a one-card launch screen.
        if (!next && reachableIds.length === 1) next = reachableIds[0];

        setDivisionState(next);
        if (next) {
          window.localStorage.setItem(cacheKey(currentUser), next);
          // Persist when the server didn't already hold this exact value —
          // covers auto-pick, cache backfill, and stale-value reset.
          if (next !== serverValue) usersApi.setDivision(next).catch(() => {});
        } else {
          window.localStorage.removeItem(cacheKey(currentUser));
        }
      } catch {
        // Network/permission failure — fall back to whatever we hydrated.
      } finally {
        if (!cancelled) setIsLoaded(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user]);

  const setDivision = useCallback(
    (id: DivisionId) => {
      // Guard: never select a division the user can't reach (the UI already only
      // offers reachable ones, and the server rejects it too).
      if (!canReachDivision(user?.divisions, id)) {
        console.warn('Ignoring attempt to select an unreachable division', id);
        return;
      }
      const userId = user?.userId;
      setDivisionState(id);
      if (userId) window.localStorage.setItem(cacheKey(userId), id);
      // Fire-and-forget; the optimistic state already updated.
      usersApi.setDivision(id).catch((err) => {
        console.warn('Failed to persist division selection', err);
      });
    },
    [user],
  );

  const value = useMemo<DivisionContextValue>(
    () => ({
      division,
      currentDivision: getDivision(division) ?? null,
      setDivision,
      isLoaded,
      availableDivisions,
      divisions: DIVISIONS,
    }),
    [division, setDivision, isLoaded, availableDivisions],
  );

  return <DivisionContext.Provider value={value}>{children}</DivisionContext.Provider>;
}

export function useDivision(): DivisionContextValue {
  const ctx = useContext(DivisionContext);
  if (!ctx) {
    throw new Error('useDivision must be used within a DivisionProvider');
  }
  return ctx;
}
